/**
 * Shared Backlog-issue-link UI used by both the WBS detail panel and
 * the migration-task detail panel.
 *
 * Behaviour contract (matches the WBS implementation that landed in
 * PR #16 / #29):
 *   - Renders one chip per issue key with a status badge from the
 *     project-level backlog cache and an external-link anchor when a
 *     URL is resolvable.
 *   - The add input accepts a raw key ("MIG-123") OR a full Backlog
 *     URL ("https://.../view/MIG-123") and normalises to a key.
 *   - A `<datalist>` of cached-but-unlinked issues offers autocomplete;
 *     options are annotated when the issue is already linked from
 *     another row so users notice double-uses.
 *
 * Persistence is the caller's job: `onAdd(key)` gets the canonical key
 * after normalisation; `onRemove(key)` gets the key the user clicked
 * the ✕ on. This keeps WBS (key-native storage) and migration tasks
 * (URL-native storage) using the same UI without forcing a backend
 * refactor on either side.
 */
import { useMemo, useState } from 'react';
import { ExternalLink, Plus, X } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { buildBacklogIssueUrl, extractIssueKey } from '../../lib/backlog';
import { cn } from '../../lib/utils';

export interface BacklogCacheEntry {
  issueKey: string;
  statusName: string;
  url: string;
  title: string;
}

export interface BacklogLinkSectionProps {
  /** Issue keys currently linked to this row. Display order is preserved. */
  issueKeys: readonly string[];
  /** Project-level Backlog cache used for status badges + URL fallback. */
  backlogCache: readonly BacklogCacheEntry[];
  /** Backlog space URL — used as a URL fallback when the cache misses. */
  spaceUrl: string | null;
  /** Stable id for the autocomplete `<datalist>`; must be unique per row. */
  datalistId: string;
  /**
   * Optional: for each cached issueKey, the human-readable list of
   * "rows that already link it" so the autocomplete option can warn
   * about double-uses. WBS supplies this; migration tasks omit it (no
   * cross-task dedupe story today).
   */
  linkedElsewhereLabelByKey?: ReadonlyMap<string, string>;
  /** Persist-on-add callback; receives the normalised issue key. */
  onAdd: (issueKey: string) => void | Promise<void>;
  /** Persist-on-remove callback; receives the issue key that was removed. */
  onRemove: (issueKey: string) => void | Promise<void>;
  /** Optional placeholder hint for the input. */
  inputPlaceholder?: string;
  /**
   * Opt-in: render the "multi-link aggregation" notice under the chip
   * list when 2 or more issues are attached. WBS uses this to remind
   * the user that status / completion roll up across linked issues;
   * migration tasks don't roll up so leave it off.
   */
  showAggregationNotice?: boolean;
}

/**
 * Accepts either a raw issue key (e.g. "MIG-123") or a full Backlog
 * URL (e.g. "https://sunup.backlog.com/view/MIG-123") and returns the
 * canonical key. URL extraction wins; otherwise the input is treated
 * as a key when it matches the {PROJECT}-{NUMBER} shape.
 *
 * Exported for testability — the parsing rule is part of the public
 * contract of this section, so we pin it explicitly.
 */
export function normalizeBacklogInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const fromUrl = extractIssueKey(trimmed);
  if (fromUrl) return fromUrl;
  if (/^[A-Z][A-Z0-9_]*-\d+$/.test(trimmed)) return trimmed;
  return null;
}

export function BacklogLinkSection({
  issueKeys,
  backlogCache,
  spaceUrl,
  datalistId,
  linkedElsewhereLabelByKey,
  onAdd,
  onRemove,
  inputPlaceholder = 'MIG-123 または Backlog URL',
  showAggregationNotice,
}: BacklogLinkSectionProps): JSX.Element {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const cacheByKey = useMemo(
    () => new Map(backlogCache.map((b) => [b.issueKey, b])),
    [backlogCache],
  );
  // Cached issues NOT yet attached to this row — these become the
  // autocomplete suggestions so users can pick by title without
  // remembering exact project keys.
  const datalistOptions = useMemo(
    () => backlogCache.filter((b) => !issueKeys.includes(b.issueKey)),
    [backlogCache, issueKeys],
  );

  const submit = async () => {
    const key = normalizeBacklogInput(draft);
    if (!key) {
      setError('課題キー (例: MIG-123) または Backlog URL を入力してください');
      return;
    }
    if (issueKeys.includes(key)) {
      setDraft('');
      setError(null);
      return;
    }
    await onAdd(key);
    setDraft('');
    setError(null);
  };

  return (
    <div data-testid="backlog-link-section">
      {issueKeys.length === 0 ? (
        <div className="text-xs text-slate-500">未紐付け</div>
      ) : (
        <div className="space-y-1">
          {issueKeys.map((k) => {
            const cached = cacheByKey.get(k);
            const href = cached?.url ?? buildBacklogIssueUrl(spaceUrl, k);
            return (
              <div
                key={k}
                className="flex items-center justify-between gap-2 rounded border border-slate-200 px-2 py-1 text-xs"
              >
                <div className="flex min-w-0 items-center gap-1.5">
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex shrink-0 items-center gap-1 text-blue-700 hover:underline"
                    >
                      <ExternalLink className="h-3 w-3 shrink-0" />
                      <span className="font-mono">{k}</span>
                    </a>
                  ) : (
                    <span className="font-mono">{k}</span>
                  )}
                  {cached?.title ? (
                    <span
                      className="min-w-0 truncate text-slate-600"
                      title={cached.title}
                    >
                      {cached.title}
                    </span>
                  ) : null}
                  {cached ? (
                    <Badge tone="blue" className={cn('shrink-0')}>
                      {cached.statusName}
                    </Badge>
                  ) : null}
                </div>
                {/*
                 * Phase 2.3 — the 12px ✕ icon is fine on a desktop
                 * pointer but a thumb misses it. The `max-lg:min-*`
                 * utilities floor the chip at 44px on mobile / tablet
                 * surfaces only; desktop keeps the compact look.
                 */}
                <button
                  aria-label="削除"
                  className="inline-flex items-center justify-center text-slate-400 hover:text-red-500 max-lg:min-h-[44px] max-lg:min-w-[44px]"
                  onClick={() => void onRemove(k)}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-2 flex items-center gap-2">
        <Input
          list={datalistOptions.length > 0 ? datalistId : undefined}
          placeholder={inputPlaceholder}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void submit();
            }
          }}
        />
        <Button
          variant="secondary"
          disabled={!draft.trim()}
          onClick={() => void submit()}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {showAggregationNotice && issueKeys.length >= 2 ? (
        <p
          data-testid="backlog-aggregation-notice"
          className="mt-1 text-[11px] text-slate-500"
        >
          2 件以上紐付けるとステータス・完了日は集約値（全件 closed → 完了）になります
        </p>
      ) : null}
      {datalistOptions.length > 0 ? (
        <datalist id={datalistId}>
          {datalistOptions.map((c) => {
            const note = linkedElsewhereLabelByKey?.get(c.issueKey) ?? '';
            return (
              <option key={c.issueKey} value={c.issueKey}>
                {c.title}
                {note ? `  ●${note}` : ''}
              </option>
            );
          })}
        </datalist>
      ) : null}
      {error ? (
        <div className="mt-1 text-xs text-red-600">{error}</div>
      ) : null}
    </div>
  );
}
